import React, { useEffect, useRef } from 'react';
import { Tab, TabList, makeStyles, shorthands } from '@fluentui/react-components';
import {
    BotSparkle20Filled,
    BotSparkle20Regular,
    ChatMultiple20Filled,
    ChatMultiple20Regular,
    DocumentBulletList20Filled,
    DocumentBulletList20Regular,
} from '@fluentui/react-icons';
import ChatRoom from '../../ChatRoom/components/ChatRoom';
import DocumentsTab from '../../Documents/components/DocumentsTab';
import OverviewTab from '../../AgentOverview/components/AgentOverviewTab';
import ChatWindowScreen from './ChatWindowScreen';
import useChatWindow, { ChatWindowTab } from '../hooks/useChatWindow';
import { customColors, customFonts } from '../../../styles';
import { useNexusDocuments } from '../../Documents/hooks/useNexusDocuments';
import { useProGPTDocuments } from '../../Documents/hooks/useProGPTDocuments';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { useAppSelector } from '../../../redux/hooks';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        backgroundColor: '#fff',
    },
    tabBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'white',
        ...shorthands.borderBottom('5px', 'solid', 'rgb(0 0 0 / 10%)'),
        ...shorthands.padding('8px', '16px'),
    },
    tabListRoot: {
        '& .fui-Tab__icon': {
            marginRight: '6px',
        },
    },
    tab: {
        ...shorthands.margin('0px', '20px'),
        '&::after': {
            backgroundColor: 'transparent',
            height: '5px',
            ...shorthands.margin('-13px'),
            ...shorthands.borderRadius('0'),
        },
        '&::before': {
            backgroundColor: 'transparent',
            height: '5px',
            ...shorthands.margin('-13px'),
            ...shorthands.borderRadius('0'),
        },
        '& > span': {
            fontFamily: customFonts.Lato,
        },
        '&[aria-selected=true]::after': {
            backgroundColor: customColors.protivitiOrange,
        },
        '&[aria-selected=true]::before': {
            backgroundColor: customColors.protivitiOrange,
        },
        '&[aria-selected=true] > span': {
            fontFamily: customFonts.Lato,
            fontWeight: '700',
            fontSize: '14px',
            lineHeight: '16px',
            color: customColors.summarizeButtonDarker,
        },
    },
    panel: {
        flex: 1,
        minHeight: 0,
    },
});

const ChatWindow: React.FC = () => {
    const styles = useStyles();
    const vm = useChatWindow();

    // Always call both hooks unconditionally
    const nexusDocuments = useNexusDocuments(vm.selectedSessionId ?? '', vm.resolvedAgent);
    const proGPTDocuments = useProGPTDocuments(vm.selectedSessionId ?? '');
    const documentsBySessionId = useAppSelector((state) => state.documents.bySessionId);
    // Track if we've already fetched documents for this session
    const fetchedSessionsRef = useRef<Set<string>>(new Set());

    // Fetch documents when Documents tab is selected for the first time
    useEffect(() => {
        if (vm.selectedTab === 'documents' && vm.selectedSessionId) {
            if (documentsBySessionId[vm.selectedSessionId] !== undefined) {
                return;
            }
            const sessionKey = `${vm.selectedSessionId}`;
            
            // Only fetch if we haven't fetched for this session yet
            if (!fetchedSessionsRef.current.has(sessionKey)) {
                fetchedSessionsRef.current.add(sessionKey);
                
                const isNexus = vm.resolvedAgent?.providerType === ChatProviderType.Nexus;
                const fetchDocuments = isNexus 
                    ? nexusDocuments.onFetchDocuments 
                    : proGPTDocuments.onFetchDocuments;
                
                void fetchDocuments();
            }
        }
    }, [vm.selectedTab, vm.selectedSessionId, vm.resolvedAgent, nexusDocuments, proGPTDocuments]);

    // Clear the fetch tracking when session changes
    useEffect(() => {
        fetchedSessionsRef.current.clear();
    }, [vm.selectedSessionId]);

    if (!vm.hasSession) {
        return <ChatWindowScreen />;
    }

    return (
        <div className={styles.root}>
            <div className={styles.tabBar}>
                <TabList
                    className={styles.tabListRoot}
                    selectedValue={vm.selectedTab}
                    onTabSelect={(_event, data) => vm.onTabSelect(data.value as ChatWindowTab)}
                >
                    {vm.visibleTabs.includes('chat') && (
                        <Tab
                            value="chat"
                            className={styles.tab}
                            icon={vm.selectedTab === 'chat' ? <ChatMultiple20Filled /> : <ChatMultiple20Regular />}
                        >
                            {vm.resolvedAgent ? `${vm.resolvedAgent.name} Chat` : 'Chat'}
                        </Tab>
                    )}
                    {vm.visibleTabs.includes('documents') && (
                        <Tab
                            value="documents"
                            className={styles.tab}
                            icon={
                                vm.selectedTab === 'documents' ? (
                                    <DocumentBulletList20Filled />
                                ) : (
                                    <DocumentBulletList20Regular />
                                )
                            }
                        >
                            Documents
                        </Tab>
                    )}
                    {vm.visibleTabs.includes('overview') && (
                        <Tab
                            value="overview"
                            className={styles.tab}
                            icon={vm.selectedTab === 'overview' ? <BotSparkle20Filled /> : <BotSparkle20Regular />}
                        >
                            Overview
                        </Tab>
                    )}
                </TabList>
            </div>

            <div className={styles.panel}>
                {vm.selectedTab === 'chat' && <ChatRoom />}
                {vm.selectedTab === 'documents' && <DocumentsTab sessionId={vm?.selectedSessionId!} resolvedAgent={vm?.resolvedAgent}/>}
                {vm.selectedTab === 'overview' && <OverviewTab agent={vm?.resolvedAgent} />}
            </div>
        </div>
    );
};

export default ChatWindow;
